import type { TFunction } from 'i18next'
import { formatMoney, time, weekdayDay, weekdayDayMonth } from '@/lib/format'
import type { CartItem, Extra, ExtraOrder } from '@/services/extras'
import type { Stay } from '@/services/reserva'
import type { Money } from '@/services/types'
import '../Pagos/Pagos.css'
import './Extras.css'

/** Rutas de extras (inventario D16: `/reserva/extras`). */
export const EXTRAS_ROUTES = {
  catalog: '/reserva/extras',
  confirm: '/reserva/extras/confirmar',
  done: '/reserva/extras/listo',
} as const

/** Carrito en la pestaña: sobrevive a ir y volver de "Confirmar y pagar" y a recargar. */
const CART_KEY = 'v2.extras.carrito'

export function readCart(guestCode: string): CartItem[] {
  try {
    const raw = sessionStorage.getItem(CART_KEY)
    const value = raw ? (JSON.parse(raw) as { guestCode: string; items: CartItem[] }) : null
    return value && value.guestCode === guestCode ? value.items : []
  } catch {
    return []
  }
}

export function writeCart(guestCode: string, items: CartItem[]): void {
  try {
    if (items.length > 0) sessionStorage.setItem(CART_KEY, JSON.stringify({ guestCode, items }))
    else sessionStorage.removeItem(CART_KEY)
  } catch {
    /* sin almacenamiento */
  }
}

/**
 * Resultado de la última contratación, para la pantalla "Listo" aunque se recargue.
 * - `paid`: contrato propuesto (mock), con recibos e información práctica que se piden al servicio.
 * - `added`: API actual (D46), los nombres de lo añadido y de lo que no.
 */
export type ExtrasResult = { guestCode: string; mode: 'paid'; checkoutId: string } | { guestCode: string; mode: 'added'; added: string[]; failed: string | null; notTried: string[] }

const RESULT_KEY = 'v2.extras.resultado'

export function writeExtrasResult(result: ExtrasResult | null): void {
  try {
    if (result) sessionStorage.setItem(RESULT_KEY, JSON.stringify(result))
    else sessionStorage.removeItem(RESULT_KEY)
  } catch {
    /* sin almacenamiento */
  }
}

export function readExtrasResult(guestCode: string): ExtrasResult | null {
  try {
    const raw = sessionStorage.getItem(RESULT_KEY)
    const value = raw ? (JSON.parse(raw) as ExtrasResult) : null
    return value && value.guestCode === guestCode ? value : null
  } catch {
    return null
  }
}

/** Personas a las que se aplica un precio por persona: las de la reserva. El resto se cuenta una vez. */
export function quantityFor(extra: Extra, stay: Pick<Stay, 'guests'>): number {
  return extra.priceUnit === 'per_person' || extra.priceUnit === 'per_person_night' ? Math.max(1, stay.guests.adults + stay.guests.children) : 1
}

export function lineAmount(extra: Extra, quantity: number): Money {
  return { amount: extra.price.amount * (extra.priceUnit === 'total' ? 1 : quantity), currency: extra.price.currency }
}

/** "45,00 €", "12,00 € / persona" o "Gratis" (PantallaExtras.dc.html). */
export function priceText(extra: Pick<Extra, 'price' | 'priceUnit'>, lang: string, t: TFunction): string {
  if (extra.price.amount === 0) return t('v2.extras.gratis')
  const price = formatMoney(extra.price, lang)
  return extra.priceUnit === 'total' ? price : t(`v2.estancia.precioUnidad.${extra.priceUnit}`, { price })
}

export const amountText = (m: Money, lang: string, t: TFunction) => (m.amount === 0 ? t('v2.extras.gratis') : formatMoney(m, lang))

/** "Desayuno · dom 4, 08:30 · 3 personas" (Servicios.dc.html cart). */
export function cartLineLabel(extra: Extra, quantity: number, lang: string, t: TFunction): string {
  const parts = [extra.name]
  if (extra.when) parts.push(`${weekdayDay(extra.when, lang)}, ${time(extra.when, lang)}`)
  if (extra.priceUnit !== 'total') parts.push(t('v2.extras.confirmar.personas', { count: quantity }))
  return parts.join(' · ')
}

/** "Transfer desde el aeropuerto · sáb 3 oct, 14:35" (Servicios.dc.html cartDone). */
export function orderTitle(order: ExtraOrder, lang: string): string {
  if (!order.when) return order.name
  return `${order.name} · ${weekdayDayMonth(order.when, lang)}, ${time(order.when, lang)}`
}

export const namesOf = (ids: string[], extras: Extra[]) => ids.map((id) => extras.find((e) => e.id === id)?.name ?? id)
